import { X } from 'lucide-react';
import Button from '../../../components/Button';
import type { QuestTemplateFirestore, QuestType } from '../services/questTypes';
import {
    ChatQuestForm,
    CommunityQuestionQuestForm,
    FeedbackQuestForm,
    LessonQuestForm,
    RateQuestForm,
    StoryQuestForm,
    ToolQuestForm
} from './quest-template-forms';
import { CheckInQuestForm } from './quest-template-forms/CheckInQuestForm';

interface EditQuestTemplateProps {
    questTemplate: QuestTemplateFirestore;
    onSave: (questTemplate: QuestTemplateFirestore) => void;
    onSaveAndContinue: (questTemplate: QuestTemplateFirestore) => void;
    onCancel: () => void;
    journeyDay: string;
    initialOrder?: number;
    questType?: QuestType;
    allQuestTemplates: QuestTemplateFirestore[];
}

export const EditQuestTemplate = ({ questTemplate, onSave, onSaveAndContinue, onCancel, journeyDay, initialOrder, questType, allQuestTemplates }: EditQuestTemplateProps) => {
    const currentQuestType = questTemplate?.type || questType || 'chat';
    const currentOrder = initialOrder ?? questTemplate?.order ?? 0;

    const renderQuestForm = () => {
        switch (currentQuestType) {
            case 'chat':
                return (
                    <ChatQuestForm
                        questTemplate={questTemplate?.type === 'chat' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );

            case 'check-in':
                return (
                    <CheckInQuestForm
                        questTemplate={questTemplate?.type === 'check-in' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );

            case 'story':
                return (
                    <StoryQuestForm
                        questTemplate={questTemplate?.type === 'story' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                        allQuestTemplates={allQuestTemplates}
                    />
                );

            case 'lesson':
                return (
                    <LessonQuestForm
                        questTemplate={questTemplate?.type === 'lesson' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                        allQuestTemplates={allQuestTemplates}
                    />
                );

            case 'community-question':
                return (
                    <CommunityQuestionQuestForm
                        questTemplate={questTemplate?.type === 'community-question' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                        allQuestTemplates={allQuestTemplates}
                    />
                );

            case 'tool':
                return (
                    <ToolQuestForm
                        questTemplate={questTemplate?.type === 'tool' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                        allQuestTemplates={allQuestTemplates}
                    />
                );

            case 'feedback':
                return (
                    <FeedbackQuestForm
                        questTemplate={questTemplate?.type === 'feedback' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );

            case 'rate':
                return (
                    <RateQuestForm
                        questTemplate={questTemplate?.type === 'rate' ? questTemplate : undefined}
                        initialOrder={currentOrder}
                        onSave={onSave}
                        onSaveAndContinue={onSaveAndContinue}
                        onCancel={onCancel}
                    />
                );

            default:
                return <div className="text-red-600">Unsupported quest type: {currentQuestType}</div>;
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600/50 z-50 flex items-center justify-center p-4">
            <div className="relative mx-auto w-full max-w-screen-xl max-h-full shadow-lg rounded-md bg-white flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between gap-2 p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
                    <div className="min-w-0">
                        <h3 className="text-lg font-medium text-gray-900">
                            {questTemplate?.id ? 'Edit Quest Template' : 'Create New Quest Template'}
                        </h3>
                        <p className="text-sm text-gray-500">Journey Day: {journeyDay}</p>
                    </div>
                    <Button
                        onClick={onCancel}
                        variant="ghost"
                        icon={<X size={24} />}
                    />
                </div>

                {/* Form Content */}
                <div className="overflow-y-auto flex-1 p-4 sm:p-6">
                    {renderQuestForm()}
                </div>

            </div>
        </div>
    );
};